import { useLargeurBloc } from './largeurBloc';

/**
 * SVG statique d'une droite graduée pour l'impression.
 *
 * Version allégée de `NumberLineElementEleve` — uniquement la géométrie, pas
 * d'état React, pas de modal de zoom, pas de thème dark. Adapté au print :
 * couleurs fixes (ink-800/ink-500), viewBox responsive, CSS scale via
 * `.ep-numberline { max-width: 100%; height: auto; }`.
 *
 * STANDARD PAPIER — lisibilité des textes : le SVG prend 100 % de son
 * conteneur, donc l'échelle vaut largeur / W. En demi-colonne (≈ 271 px), un
 * viewBox de 600 rendait les abscisses à ≈ 5 px. Le viewBox s'adapte à la
 * largeur du bloc (contexte LargeurBloc, ou prop `largeur`) : W = 300 en
 * demi-colonne → mêmes unités de police, glyphes deux fois plus grands,
 * droite de même largeur physique. Le zoom du bloc n'y change rien (SVG en
 * pourcentage).
 *
 * ⚠ L'ÉTIQUETAGE est le MIROIR EXACT de l'écran (prédicat shouldShowNumber
 * de NumberLineElementEleve) : start/end (si showStart/showEnd), reperes,
 * et points `showValue` — les autres graduations restent muettes. Toute
 * divergence ici DIVULGUE des réponses (gens 603/624 : l'abscisse cachée du
 * point EST la réponse) ou noie la droite sous les décimaux (pas
 * fractionnaires). Les fractions passent par `displayFraction` (SVG natif).
 *
 * Props :
 *   - start, end, step       : bornes + pas (nombres)
 *   - lineType?              : 'half' (avec flèche droite) | 'full' (bornée)
 *   - showArrow?             : force l'affichage/masquage de la flèche (default true si half)
 *   - showStart?, showEnd?   : étiqueter les bornes (défauts true, comme l'écran)
 *   - points?                : [{ value, label?, color?, showValue?, displayFraction? }]
 *   - reperes?               : [number] (graduations marquées ET étiquetées)
 *   - label?                 : étiquette de l'axe (affichée en haut à droite)
 *   - largeur?               : 'full' | 'half' — force la largeur de bloc (sinon contexte)
 */

const MAX_GRADUATIONS = 101;
const LINE_COLOR = '#334155';   // ink-700
const TEXT_COLOR = '#1e293b';   // ink-800
const FONT = "'Plus Jakarta Sans', sans-serif";

// Tailles (unités viewBox). Pleine largeur (échelle ≈ 1,04) : abscisses
// ≈ 13,5 px ≈ 10 pt pour un texte courant de 15,2 px.
const FONT_NUM = 13;
const FONT_POINT = 14;
const FONT_FRAC = 12;
const FONT_LABEL = 13;
const TICK = 6;
const TICK_REPERE = 9;

/** Largeur du viewBox selon la largeur du bloc. */
export const largeurViewBox = (largeur) => (largeur === 'half' ? 300 : 600);

const formatValue = (val) => {
    if (Number.isInteger(val)) return String(val);
    return Number(val.toFixed(4)).toString().replace('.', ',');
};

const getGraduations = (start, end, step) => {
    if (step <= 0 || start >= end) return [];
    const count = Math.round((end - start) / step) + 1;
    const eff = Math.min(count, MAX_GRADUATIONS);
    const effStep = (end - start) / (eff - 1);
    const grads = [];
    for (let k = 0; k < eff; k++) {
        const val = start + k * effStep;
        grads.push(Math.round(val * 1e9) / 1e9);
    }
    return grads;
};

// Valeur d'un point en fraction empilée — SVG natif (deux <text> + barre),
// pas de foreignObject : plus sûr au moteur d'impression.
const FractionSvg = ({ x, y, num, den, color = TEXT_COLOR }) => (
    <g fontFamily={FONT} fill={color}>
        <text x={x} y={y} textAnchor="middle" fontSize={FONT_FRAC} fontWeight="600">{num}</text>
        <line x1={x - 8} y1={y + 3} x2={x + 8} y2={y + 3} stroke={color} strokeWidth={1.2} />
        <text x={x} y={y + 15} textAnchor="middle" fontSize={FONT_FRAC} fontWeight="600">{den}</text>
    </g>
);

const PrintNumberLine = ({
    start = 0,
    end = 10,
    step = 1,
    lineType = 'half',
    showArrow,
    showStart = true,
    showEnd = true,
    points = [],
    reperes = [],
    label = '',
    largeur,
}) => {
    const largeurBloc = useLargeurBloc();
    const isFull = lineType === 'full';
    const showArr = showArrow != null ? showArrow : !isFull;
    const W = largeurViewBox(largeur || largeurBloc);
    const H = 96;
    const lineY = 44;
    const padL = isFull ? 40 : 25;
    const padR = showArr ? 35 : 15;
    const usableW = W - padL - padR;

    if (step <= 0 || start >= end) return null;

    const toX = (v) => padL + ((v - start) / (end - start)) * usableW;
    const grads = getGraduations(start, end, step);
    const reperesSet = new Set((reperes || []).map((v) => Math.round(v * 1000)));
    const findPoint = (val) => (points || []).find((p) => Math.abs(p.value - val) < 0.001);

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            className="ep-numberline"
            preserveAspectRatio="xMidYMid meet"
            style={{ '--ep-svg-w': `${W}px` }}
        >
            {/* Extension à gauche pour droite complète */}
            {isFull && (
                <line x1={padL - 18} y1={lineY} x2={padL} y2={lineY} stroke={LINE_COLOR} strokeWidth={1.2} />
            )}
            {/* Ligne principale */}
            <line
                x1={padL}
                y1={lineY}
                x2={W - padR + (showArr ? 8 : 0)}
                y2={lineY}
                stroke={LINE_COLOR}
                strokeWidth={1.4}
            />
            {/* Flèche */}
            {showArr && (
                <polygon
                    points={`${W - padR + 15},${lineY} ${W - padR + 6},${lineY - 4.5} ${W - padR + 6},${lineY + 4.5}`}
                    fill={LINE_COLOR}
                />
            )}
            {/* Graduations + labels + points */}
            {grads.map((val, i) => {
                const x = toX(val);
                const isRepere = reperesSet.has(Math.round(val * 1000));
                const tick = isRepere ? TICK_REPERE : TICK;
                const point = findPoint(val);
                // Prédicat d'étiquetage : MIROIR de l'écran (Eleve l.147-151).
                // Inconditionnel avant : toutes les abscisses sortaient au
                // papier, y compris celle que l'exercice demande de trouver.
                const shouldShowNumber =
                    (i === 0 && showStart) ||
                    (i === grads.length - 1 && showEnd) ||
                    (point && point.showValue) ||
                    isRepere;
                return (
                    <g key={i}>
                        <line
                            x1={x}
                            y1={lineY - tick}
                            x2={x}
                            y2={lineY + tick}
                            stroke={LINE_COLOR}
                            strokeWidth={isRepere ? 1.5 : 1.2}
                        />
                        {shouldShowNumber && (
                            point && point.displayFraction ? (
                                /* Même règle que renderSvgValue écran : une
                                   graduation étiquetée portant un point rend
                                   la valeur DU POINT (fraction empilée) */
                                <FractionSvg
                                    x={x}
                                    y={lineY + 22}
                                    num={point.displayFraction.num}
                                    den={point.displayFraction.den}
                                    color={point.color || TEXT_COLOR}
                                />
                            ) : (
                                <text
                                    x={x}
                                    y={lineY + 24}
                                    textAnchor="middle"
                                    fontSize={FONT_NUM}
                                    fill={TEXT_COLOR}
                                    fontFamily={FONT}
                                >
                                    {formatValue(point ? (point.value ?? val) : val)}
                                </text>
                            )
                        )}
                        {point && (
                            <>
                                <circle
                                    cx={x}
                                    cy={lineY}
                                    r={4.5}
                                    fill={point.color || TEXT_COLOR}
                                    stroke="white"
                                    strokeWidth={1}
                                />
                                {point.label && (
                                    <text
                                        x={x}
                                        y={lineY - 13}
                                        textAnchor="middle"
                                        fontSize={FONT_POINT}
                                        fontWeight="700"
                                        fill={point.color || TEXT_COLOR}
                                        fontFamily={FONT}
                                    >
                                        {point.label}
                                    </text>
                                )}
                            </>
                        )}
                    </g>
                );
            })}
            {/* Label d'axe (optionnel) */}
            {label && (
                <text
                    x={W - padR + 20}
                    y={lineY + 5}
                    fontSize={FONT_LABEL}
                    fontStyle="italic"
                    fill={TEXT_COLOR}
                    fontFamily={FONT}
                >
                    {label}
                </text>
            )}
        </svg>
    );
};

export default PrintNumberLine;
